/**
 * E2 — refunds and net, under the Revenue KPI row.
 *
 * THE KPI TILES ABOVE ARE GROSS AND STAY GROSS. `totals` did not change
 * meaning when this block was added: every legacy key is byte-stable, and the
 * "Revenue in range" tile still shows what was taken. Net is stated separately,
 * beneath, where it can carry its own explanation — a net figure printed under
 * an unchanged label would silently redefine a number twenty saved screenshots
 * already disagree with.
 *
 * NET IS THE SERVER'S ARITHMETIC, NOT OURS. `net_cents_in_range` arrives
 * computed and can legitimately be NEGATIVE: refunds are bucketed by
 * `refunded_at` (a cash basis), so a refund issued this week for a purchase
 * made last quarter subtracts from a window it never added to. Re-deriving it
 * here is how two pages come to disagree about one window, so this component
 * never subtracts anything.
 *
 * THE ALL-TIME RATE'S DENOMINATOR IS THE POINT. `refund_rate_all_time` divides
 * by `status IN ('paid','refunded')` — never 'paid' alone. `wedding_purchases`
 * has ONE status column, so refunding a purchase MOVES the row out of 'paid':
 * a refunded/paid rate divides by a denominator the numerator has already been
 * subtracted from. The caption prints "A of B" so the denominator is visible,
 * and an em dash — never a 0% — when the rate is null because nothing has ever
 * settled.
 *
 * `refunds` IS OPTIONAL ON THE TYPE. An older backend that predates this key
 * must render NOTHING here rather than crash or print zeros it never measured.
 */

import React from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { RevenueRefunds } from '../weddingApi';
import { centsToUsd } from '../weddingFormat';

interface Props {
  refunds?: RevenueRefunds | null;
  /** Human label for the selected window, e.g. "the last 90 days". */
  rangeLabel?: string;
}

/** Server-shipped rate (0..1) → "4.8%". Null stays null; the caller decides. */
function ratePct(rate: number | null | undefined): string | null {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return null;
  return `${Math.round(rate * 1000) / 10}%`;
}

const RefundCaption: React.FC<Props> = ({ refunds, rangeLabel }) => {
  // Feature detection, not a zero: this backend does not report refunds.
  if (!refunds) return null;

  const hadRefunds = refunds.count_in_range > 0;
  const rate = ratePct(refunds.refund_rate_all_time);

  return (
    <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }} data-testid="wedding-refund-caption">
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {hadRefunds ? (
          <Typography variant="body2" color="text.secondary">
            <Typography component="span" variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
              − {centsToUsd(refunds.cents_in_range)} refunded
            </Typography>
            {rangeLabel ? ` in ${rangeLabel}` : ' in range'}
            {` across ${refunds.count_in_range} purchase${refunds.count_in_range === 1 ? '' : 's'} · net `}
            <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
              {centsToUsd(refunds.net_cents_in_range)}
            </Typography>
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No refunds in this range.
          </Typography>
        )}
        <Tooltip title={refunds.basis_note}>
          <InfoOutlinedIcon fontSize="small" color="disabled" />
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {rate === null ? (
          /*
           * AN EM DASH, NOT 0%. A null rate means nothing has ever settled —
           * there is no denominator, so there is no rate, and printing 0%
           * would claim a measurement that was never taken.
           */
          <>— of settled purchases have ever been refunded (no purchase has settled yet)</>
        ) : (
          <>
            {rate} of settled purchases have ever been refunded (
            {refunds.refunded_count_all_time} of {refunds.ever_settled_count})
          </>
        )}
      </Typography>

      {refunds.refunded_without_timestamp > 0 && (
        <Typography variant="caption" color="text.secondary">
          {refunds.refunded_without_timestamp} refunded purchase
          {refunds.refunded_without_timestamp === 1 ? ' has' : 's have'} no refund timestamp, so
          {refunds.refunded_without_timestamp === 1 ? ' it appears' : ' they appear'} in no window
          here — only in the all-time figure.
        </Typography>
      )}

      {/* The server's own sentence about the basis, rendered rather than paraphrased. */}
      <Typography variant="caption" color="text.disabled">
        {refunds.basis_note}
      </Typography>
    </Stack>
  );
};

export default RefundCaption;
